import type { ExternalStatus } from '@hibro/shared';
import { Button, Icon } from '@hibro/ui-client';
import { type ReactNode, useEffect, useRef, useState } from 'react';

import { useT } from '../i18n/i18n';

/**
 * Bridge to the main-process `ExternalProcessHost` (架构方案 §3.1/§5.8). Injected
 * by the app shell over the preload `window.hibroExternal` IPC bridge so the
 * platform package stays free of it (mirrors `IframeBridge`). Drives the
 * external app's lifecycle and the platform -> external navigation (见 8).
 */
export interface ExternalBridge {
  launch(processKey: string): Promise<ExternalStatus>;
  activate(processKey: string): Promise<void>;
  close(processKey: string): Promise<void>;
  status(processKey: string): Promise<ExternalStatus>;
  /**
   * Subscribe to process-table changes (a process started or exited), so a panel
   * can re-read `status` instead of polling. Backs `ViewProps.external
   * .onDidChangeStatus`; optional because a shell may wire only the calls.
   */
  onDidChange?(listener: () => void): () => void;
}

export interface ExternalSurfaceProps {
  /**
   * Which process this panel controls (架构方案 §3.1/§5.8):
   * `externalProcessKey(moduleId, view.externalEntry)` — the bare module id for a
   * single-entry module, `<moduleId>#<entryId>` for one of several. Every bridge
   * call is keyed by it, so two panels of the same module drive two processes.
   */
  processKey: string;
  bridge: ExternalBridge;
  /**
   * Whether to spawn the process on mount (架构方案 §3.1/§5.8). Default lazy
   * (`false`): the panel opens idle and only the explicit Launch button (or a
   * focus while already running) starts it. Resolved from the bound
   * `entry.external` entry's `autoLaunch` and threaded through {@link SurfaceView}.
   */
  autoLaunch?: boolean;
}

type BusyAction = 'launch' | 'focus' | 'close';

/**
 * Failures stay on the panel and in the log. A swallowed rejection used to
 * leave the buttons looking idle after Launch had already failed.
 */
function reportExternalFailure(
  processKey: string,
  action: string,
  err: unknown,
): string {
  const message = err instanceof Error ? err.message : String(err);
  (
    globalThis as {
      hibroLog?: {
        append: (entry: {
          level: string;
          kind: string;
          message: string;
        }) => void;
      };
    }
  ).hibroLog?.append({
    level: 'error',
    kind: 'log',
    message: `external ${action} failed for ${processKey}: ${message}`,
  });
  return message;
}

/**
 * The `external` SurfaceHost (架构方案 §3.1/§5.8): an editor-area control panel
 * for an external-process module (e.g. an IDE). Unlike inline/embedded surfaces
 * there is no in-window content — the external app is its own native window — so
 * this panel offers Launch when idle, and Focus / Close window once it is
 * running, plus a live status (running/pid, and the last reverse
 * `executeCommand` when one has happened). By default the process is NOT
 * spawned on mount (lazy); pass `autoLaunch` to bring it up as the tab opens.
 * Closing the tab does NOT kill the process (an IDE outlives its launcher
 * tab); only the explicit Close window button does.
 */
export function ExternalSurface({
  processKey,
  bridge,
  autoLaunch = false,
}: ExternalSurfaceProps): ReactNode {
  const t = useT();
  const alive = useRef(true);
  const keyRef = useRef(processKey);
  const revision = useRef(0);
  const actionId = useRef(0);
  keyRef.current = processKey;

  const [snapshot, setSnapshot] = useState<ExternalStatus | null>(null);
  const [busy, setBusy] = useState<BusyAction | null>(null);
  const [error, setError] = useState<string | undefined>(undefined);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    const rev = ++revision.current;
    setSnapshot(null);
    setBusy(null);
    setError(undefined);

    const apply = (token: number, value: ExternalStatus): void => {
      if (!active || revision.current !== token) {
        return;
      }
      setSnapshot(value);
      setError(undefined);
    };
    const fail = (token: number, action: string, err: unknown): void => {
      if (!active || revision.current !== token) {
        return;
      }
      setError(reportExternalFailure(processKey, action, err));
    };

    const initial = autoLaunch
      ? bridge.launch(processKey)
      : bridge.status(processKey);
    initial.then(
      (value) => apply(rev, value),
      (err: unknown) => fail(rev, autoLaunch ? 'launch' : 'status', err),
    );

    // A later read (the process exited on its own) must not be overwritten by
    // the in-flight mount request, and must not itself overwrite a newer click.
    const dispose = bridge.onDidChange?.(() => {
      const token = ++revision.current;
      bridge.status(processKey).then(
        (value) => apply(token, value),
        (err: unknown) => fail(token, 'status', err),
      );
    });

    return () => {
      active = false;
      dispose?.();
    };
  }, [autoLaunch, bridge, processKey]);

  const run = (
    action: BusyAction,
    task: () => Promise<ExternalStatus>,
  ): void => {
    const id = ++actionId.current;
    const key = processKey;
    const token = ++revision.current;
    setBusy(action);
    setError(undefined);
    void task()
      .then((value) => {
        if (
          !alive.current ||
          keyRef.current !== key ||
          revision.current !== token
        ) {
          return;
        }
        setSnapshot(value);
        setError(undefined);
      })
      .catch((err: unknown) => {
        if (
          !alive.current ||
          keyRef.current !== key ||
          revision.current !== token
        ) {
          return;
        }
        setError(reportExternalFailure(key, action, err));
      })
      .finally(() => {
        if (alive.current && actionId.current === id) {
          setBusy(null);
        }
      });
  };

  const running = snapshot?.running === true;
  const launching =
    busy === 'launch' ||
    (snapshot === null && autoLaunch && error === undefined);
  const showActions = snapshot !== null || error !== undefined;
  const lastCommand = snapshot?.lastReverse?.command;

  return (
    <div className="flex h-full min-h-0 w-full flex-1 flex-col items-center justify-center gap-4 p-8 text-center">
      <div className="flex size-12 items-center justify-center rounded-full bg-muted text-muted-foreground">
        <Icon spec={{ type: 'builtin', id: 'external-link' }} size={22} />
      </div>
      <div className="flex max-w-sm flex-col gap-1">
        <p className="text-sm font-medium text-foreground">
          {t('external.title')}
        </p>
        <p className="text-[13px] text-muted-foreground">
          {t('external.desc')}
        </p>
      </div>
      <div
        role="status"
        aria-live="polite"
        className="flex h-7 items-center justify-center"
      >
        {snapshot === null && error === undefined && !launching ? (
          <Icon
            className="animate-spin text-muted-foreground"
            spec={{ type: 'builtin', id: 'loader' }}
            size={16}
          />
        ) : (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-muted px-3 text-[12px] leading-7 text-muted-foreground">
            {launching ? (
              <Icon
                className="animate-spin"
                spec={{ type: 'builtin', id: 'loader' }}
                size={12}
              />
            ) : (
              <span
                className={
                  running
                    ? 'size-1.5 rounded-full bg-success'
                    : 'size-1.5 rounded-full bg-muted-foreground opacity-40'
                }
              />
            )}
            <span>
              {launching
                ? t('external.status.launching')
                : running
                  ? t('external.status.running')
                  : t('external.status.idle')}
            </span>
            {running && !launching && snapshot?.pid !== undefined ? (
              <>
                <span aria-hidden="true">·</span>
                <span>
                  {t('external.status.pid', { pid: String(snapshot.pid) })}
                </span>
              </>
            ) : null}
          </span>
        )}
      </div>
      {showActions ? (
        <div className="flex gap-2">
          {running && !launching ? (
            <>
              <Button
                size="sm"
                disabled={busy !== null}
                onClick={() =>
                  run('focus', async () => {
                    await bridge.activate(processKey);
                    return bridge.status(processKey);
                  })
                }
              >
                {t('external.action.focus')}
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={busy !== null}
                onClick={() =>
                  run('close', async () => {
                    await bridge.close(processKey);
                    return bridge.status(processKey);
                  })
                }
              >
                {t('external.action.close')}
              </Button>
            </>
          ) : (
            <Button
              size="sm"
              disabled={busy !== null || launching}
              onClick={() => run('launch', () => bridge.launch(processKey))}
            >
              {t('external.action.launch')}
            </Button>
          )}
        </div>
      ) : null}
      {lastCommand ? (
        <p className="max-w-md truncate text-[12px] text-muted-foreground">
          {t('external.status.reverse', { command: lastCommand })}
        </p>
      ) : null}
      {error ? (
        <p className="max-w-sm text-[13px] text-destructive">{error}</p>
      ) : null}
    </div>
  );
}
